import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { includesAddress } from './recipientAddresses';
import { ScheduleSetting, ScheduleSettingPayload } from '../../../store/ScheduleSettings';
import {
  buildScheduleRule,
  isCalendarDate,
  NEW_SCHEDULE_RULE,
  parseScheduleRule,
  ParsedScheduleRule,
  ScheduleForm
} from './scheduleRule';

const today = () => format(new Date(), 'yyyy-MM-dd');

export type ScheduleFields = Pick<
  ScheduleSettingPayload,
  'schedule_rule' | 'start_date' | 'end_date' | 'recipients' | 'send_email' | 'share_with_team'
>;

export interface UseScheduleFormReturn {
  cadence: ParsedScheduleRule;
  /** The existing schedule's cadence; null for a new schedule. */
  savedCadence: ParsedScheduleRule | null;
  form: ScheduleForm;
  startDate: string;
  endDate: string;
  noEndDate: boolean;
  recipients: string[];
  recipientInput: string;
  /** What the recipients field last read but did not add; while any is named, the schedule is not saved. */
  notAdded: string[];
  sendToSelf: boolean;
  /** False while the start date, or an end date that is in use, is not a calendar date. */
  datesValid: boolean;
  /** The end date Save sends: null with No end date, an empty field or an unreadable date. */
  endDateValue: string | null;
  /** The rule Save would send, for the preview; null while the start date is not a calendar date. */
  rebuiltRule: Record<string, unknown> | null;
  updateCadence: (patch: Partial<ParsedScheduleRule>) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setNoEndDate: (noEndDate: boolean) => void;
  setRecipients: (recipients: string[]) => void;
  setRecipientInput: (input: string) => void;
  setNotAdded: (words: string[]) => void;
  setSendToSelf: (sendToSelf: boolean) => void;
  reseed: () => void;
  scheduleFields: () => ScheduleFields;
}

/**
 * The cadence, dates and recipients a schedule form edits, seeded from `existingSchedule` by `reseed`.
 * The form calls `reseed` itself, so it decides when a changed schedule may replace what was typed.
 */
export const useScheduleForm = (
  existingSchedule: ScheduleSetting | null,
  currentUserEmail: string
): UseScheduleFormReturn => {
  const savedCadence = useMemo(
    () => (existingSchedule ? parseScheduleRule(existingSchedule.schedule_rule) : null),
    [existingSchedule]
  );

  const [cadence, setCadence] = useState<ParsedScheduleRule>(NEW_SCHEDULE_RULE);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [noEndDate, setNoEndDate] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [notAdded, setNotAdded] = useState<string[]>([]);
  const [sendToSelf, setSendToSelf] = useState(false);

  const reseed = () => {
    setRecipientInput('');
    setNotAdded([]);
    if (existingSchedule) {
      setCadence(savedCadence ?? NEW_SCHEDULE_RULE);
      setStartDate(existingSchedule.start_date);
      setEndDate(existingSchedule.end_date || '');
      setNoEndDate(!existingSchedule.end_date);
      setRecipients(existingSchedule.recipients.filter((r) => !includesAddress([currentUserEmail], r)));
      setSendToSelf(includesAddress(existingSchedule.recipients, currentUserEmail));
    } else {
      setCadence(NEW_SCHEDULE_RULE);
      setStartDate(today());
      setEndDate('');
      setNoEndDate(false);
      setRecipients([]);
      setSendToSelf(false);
    }
  };

  const form: ScheduleForm = { ...cadence, startDate };
  const updateCadence = (patch: Partial<ParsedScheduleRule>) => setCadence((current) => ({ ...current, ...patch }));
  const hasStartDate = isCalendarDate(startDate);
  // A year past four digits gets through this input as it does the start's, and would break where the date is shown.
  const endDateUnreadable = !noEndDate && !!endDate && !isCalendarDate(endDate);
  const endDateValue = noEndDate || endDateUnreadable ? null : endDate || null;
  // Without a readable start date the rule starts from the clock, and each render would ask for dates Save won't send.
  const rebuiltRule = hasStartDate ? buildScheduleRule(form) : null;
  const datesValid = hasStartDate && !endDateUnreadable;

  const scheduleFields = (): ScheduleFields => {
    // The user's own address as their account has it, which is what the API matches a portal user by.
    const recipientList =
      sendToSelf && currentUserEmail
        ? [...recipients.filter((recipient) => !includesAddress([currentUserEmail], recipient)), currentUserEmail]
        : [...recipients];
    return {
      schedule_rule: buildScheduleRule(form),
      start_date: startDate,
      end_date: endDateValue,
      recipients: recipientList,
      send_email: true,
      share_with_team: false
    };
  };

  return {
    cadence,
    savedCadence,
    form,
    startDate,
    endDate,
    noEndDate,
    recipients,
    recipientInput,
    notAdded,
    sendToSelf,
    datesValid,
    endDateValue,
    rebuiltRule,
    updateCadence,
    setStartDate,
    setEndDate,
    setNoEndDate,
    setRecipients,
    setRecipientInput,
    setNotAdded,
    setSendToSelf,
    reseed,
    scheduleFields
  };
};
